import { useEffect, useMemo, useState } from "react";
import { IRole, IUserRole } from "../models/IUserRoles";
import { assignUserRole, getRoleList, setUserRoleStatus } from "../services/auth";
import { invalidateSalonRoleCache, SUPER_ADMIN_ROLE_ID } from "../services/checkRole";
import { getUserToken } from "../utils/Util";

type RolesState = IUserRole[] | "loading" | "error";

function toRoleArray(data: unknown): IRole[] {
  if (Array.isArray(data)) return data as IRole[];
  const wrapped = (data as { roleList?: IRole[] } | null)?.roleList;
  return Array.isArray(wrapped) ? wrapped : [];
}

function errorText(err: any, fallback: string): string {
  if (err?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  const data = err?.response?.data;
  // Backend "zaten atanmış" durumunu yanlış adla (UserRoleNotFound) döndürüyor.
  if (Array.isArray(data) && data[0]?.code === "UserRoleNotFound") return "Bu rol kullanıcıda zaten tanımlı.";
  if (Array.isArray(data) && data[0]?.description) return data[0].description;
  return fallback;
}

interface Props {
  userId: string;
  userName: string;
  roles?: RolesState;
  /** Atama/aktifleştirme sonrası kullanıcının rolleri yeniden çekilsin. */
  onChanged: () => Promise<void> | void;
}

export default function UserRoleAssigner({ userId, userName, roles, onChanged }: Props) {
  const [allRoles, setAllRoles] = useState<IRole[] | null>(null);
  const [listError, setListError] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [confirmSuper, setConfirmSuper] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // "assign" ya da userRoleId
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    getRoleList()
      .then((res) => setAllRoles(toRoleArray(res.data)))
      .catch(() => setListError("Rol listesi alınamadı."));
  }, []);

  const userRoles = useMemo(() => (Array.isArray(roles) ? roles : []), [roles]);
  const assignedIds = useMemo(
    () => new Set(userRoles.map((r) => r.roleId.toLowerCase())),
    [userRoles]
  );

  // Atanabilir: sistemde aktif ve kullanıcıda hiç olmayan roller.
  const assignable = useMemo(
    () => (allRoles ?? [])
      .filter((r) => r.roleIsEnabled && !assignedIds.has(r.id.toLowerCase()))
      .sort((a, b) => (a.roleName ?? "").localeCompare(b.roleName ?? "", "tr")),
    [allRoles, assignedIds]
  );

  // Kullanıcıda var ama pasif: yeniden atanamaz, ancak aktifleştirilebilir.
  const inactive = userRoles.filter((r) => r.userRoleIsEnabled === false);

  const roleName = (roleId: string) =>
    (allRoles ?? []).find((r) => r.id.toLowerCase() === roleId.toLowerCase())?.roleName ?? "Rol";

  const isSuper = selectedRoleId.toLowerCase() === SUPER_ADMIN_ROLE_ID;

  const doAssign = async () => {
    if (!selectedRoleId) return;
    if (isSuper && !confirmSuper) { setConfirmSuper(true); return; }
    setBusy("assign");
    setError("");
    setSuccess("");
    try {
      await assignUserRole(userId, selectedRoleId);
      invalidateSalonRoleCache();
      setSuccess(`"${roleName(selectedRoleId)}" rolü atandı.`);
      setSelectedRoleId("");
      setConfirmSuper(false);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Rol atanamadı."));
    } finally {
      setBusy(null);
    }
  };

  const activate = async (ur: IUserRole) => {
    setBusy(ur.id);
    setError("");
    setSuccess("");
    try {
      await setUserRoleStatus(ur.id, true);
      invalidateSalonRoleCache();
      setSuccess(`"${ur.role?.roleName ?? roleName(ur.roleId)}" rolü yeniden aktifleştirildi.`);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Rol aktifleştirilemedi."));
    } finally {
      setBusy(null);
    }
  };

  // Kaldırma = pasifleştirme (backend'de silme ucu yok). Kaldırılan rol "Pasif roller"e düşer
  // ve oradan geri açılabilir.
  const active = userRoles
    .filter((r) => r.userRoleIsEnabled !== false)
    .slice()
    .sort((a, b) => (a.role?.roleName ?? roleName(a.roleId)).localeCompare(b.role?.roleName ?? roleName(b.roleId), "tr"));

  const isSelf = (getUserToken()?.userId ?? "").toLowerCase() === userId.toLowerCase();
  // Kendi SuperAdmin rolünü kaldırmak yönetici ekranlarına erişimi kapatır (tek SuperAdmin
  // sensen geri dönüşü olmaz); bu yüzden buradan yapılamaz.
  const isLockedOut = (ur: IUserRole) => isSelf && ur.roleId.toLowerCase() === SUPER_ADMIN_ROLE_ID;

  const remove = async (ur: IUserRole) => {
    setBusy(ur.id);
    setError("");
    setSuccess("");
    try {
      await setUserRoleStatus(ur.id, false);
      invalidateSalonRoleCache();
      setSuccess(`"${ur.role?.roleName ?? roleName(ur.roleId)}" rolü kaldırıldı.`);
      setConfirmRemoveId(null);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Rol kaldırılamadı."));
    } finally {
      setBusy(null);
    }
  };

  const rolesReady = Array.isArray(roles);

  return (
    <div className="ura">
      <div className="ura__title"><i className="bi bi-plus-circle-fill me-2"></i>Rol Ekle</div>

      {listError ? (
        <div className="ura__msg ura__msg--error"><i className="bi bi-exclamation-circle-fill"></i>{listError}</div>
      ) : !allRoles || !rolesReady ? (
        <div className="ura__loading"><span className="spinner-border spinner-border-sm" />Yükleniyor…</div>
      ) : (
        <>
          {assignable.length === 0 ? (
            <div className="ura__empty">Atanabilecek başka aktif rol yok.</div>
          ) : (
            <div className="ura__row">
              <select className="form-select ura__select" value={selectedRoleId} disabled={busy !== null}
                onChange={(e) => { setSelectedRoleId(e.target.value); setConfirmSuper(false); setError(""); setSuccess(""); }}>
                <option value="">Rol seçin…</option>
                {assignable.map((r) => (
                  <option key={r.id} value={r.id}>{r.roleName}</option>
                ))}
              </select>
              <button type="button" className={`ura__btn${isSuper && confirmSuper ? " ura__btn--danger" : ""}`}
                disabled={!selectedRoleId || busy !== null} onClick={doAssign}>
                {busy === "assign"
                  ? <span className="spinner-border spinner-border-sm" />
                  : isSuper && confirmSuper ? <>Evet, ata</> : <><i className="bi bi-check-lg"></i>Ata</>}
              </button>
            </div>
          )}

          {selectedRoleId && (
            <div className="ura__desc">
              {(allRoles.find((r) => r.id === selectedRoleId)?.roleDescription || "").trim() || "Açıklama yok."}
            </div>
          )}

          {isSuper && confirmSuper && (
            <div className="ura__msg ura__msg--warn">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span><strong>{userName}</strong> sistemdeki tüm restoranlara ve yönetim ekranlarına tam erişim kazanacak. Emin misin?</span>
            </div>
          )}

          {/* Rol kaldır */}
          <div className="ura__title ura__title--remove"><i className="bi bi-dash-circle-fill me-2"></i>Rol Kaldır</div>
          {active.length === 0 ? (
            <div className="ura__empty">Kullanıcının aktif rolü yok.</div>
          ) : (
            <div className="ura__active">
              {active.map((ur) => {
                const name = ur.role?.roleName ?? roleName(ur.roleId);
                const confirming = confirmRemoveId === ur.id;
                const locked = isLockedOut(ur);
                const isSuperRole = ur.roleId.toLowerCase() === SUPER_ADMIN_ROLE_ID;
                return (
                  <div key={ur.id} className={`ura__active-row${confirming ? " ura__active-row--confirm" : ""}`}>
                    {confirming ? (
                      <>
                        <span className="ura__confirm-text">
                          <strong>{name}</strong> kaldırılsın mı?
                          {isSuperRole && <em> Tüm restoranlara erişimi kapanır.</em>}
                          {isSelf && !isSuperRole && <em> Bu senin rolün; ilgili ekranlara erişimini kaybedebilirsin.</em>}
                        </span>
                        <div className="ura__confirm-actions">
                          <button type="button" className="ura__link" disabled={busy !== null}
                            onClick={() => setConfirmRemoveId(null)}>Vazgeç</button>
                          <button type="button" className="ura__btn ura__btn--danger ura__btn--sm" disabled={busy !== null}
                            onClick={() => remove(ur)}>
                            {busy === ur.id ? <span className="spinner-border spinner-border-sm" /> : "Evet, kaldır"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span>{name}</span>
                        <button type="button" className="ura__link ura__link--danger"
                          disabled={busy !== null || locked}
                          title={locked ? "Kendi SuperAdmin rolünü buradan kaldıramazsın." : undefined}
                          onClick={() => { setConfirmRemoveId(ur.id); setError(""); setSuccess(""); }}>
                          {locked ? <><i className="bi bi-lock-fill me-1"></i>Kilitli</> : "Kaldır"}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {inactive.length > 0 && (
            <div className="ura__inactive">
              <div className="ura__sub">Pasif roller</div>
              {inactive.map((ur) => (
                <div key={ur.id} className="ura__inactive-row">
                  <span>{ur.role?.roleName ?? roleName(ur.roleId)}</span>
                  <button type="button" className="ura__link" disabled={busy !== null} onClick={() => activate(ur)}>
                    {busy === ur.id ? <span className="spinner-border spinner-border-sm" /> : "Aktifleştir"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {error && <div className="ura__msg ura__msg--error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}
      {success && <div className="ura__msg ura__msg--ok"><i className="bi bi-check-circle-fill"></i>{success}</div>}
    </div>
  );
}
